import { FC, useState } from 'react'

import Close from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format, isValid } from 'date-fns'
import { bg, de } from 'date-fns/locale'
import i18n from 'i18n'

import { useField, useFormikContext } from 'formik'

import { ELEMENT_RADIUS_MID } from 'utils/customizedTheme'
import {  parseDateTime } from 'utils/helpers'

interface IProps {
  fieldProps: TextFieldProps
  valueSelector?: string
  sx?: any
  onChange?: any
  disabled?: boolean
  maxDate?: Date
  minDate?: Date
}

const DateTimePickerField: FC<IProps> = ({ 
    fieldProps,
    valueSelector, 
    onChange, 
    sx = {}, 
    disabled, 
    maxDate, 
    minDate,
  }) => {
  const [field, meta] = useField(fieldProps.name)
  const { setFieldValue } = useFormikContext()
  const { touched, error } = meta
  const renderHelperText = () => {
    if (touched && error) {
      return error
    }
  }

  const locale = {
    bg,
    de,
  }

  const [open, setOpen] = useState(false)
  const [showClear, setShowClear] = useState(false)

  const dateTimeValue = valueSelector ? field.value[valueSelector] : field.value
  const value =  dateTimeValue ? parseDateTime(dateTimeValue) : null

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale[i18n.language]}>
      <DateTimePicker
        maxDate={maxDate}
        minDate={minDate}
        open={open}
        value={value}
        disabled={disabled}
        inputFormat='dd-MM-yyyy HH:mm'
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={(value) => {
          let newValue = isValid(value) ? format(value, 'yyyy-MM-dd HH:mm') : value

          if (valueSelector) {
            newValue = {
              ...field.value,
              [valueSelector]: newValue,
            }
          }

          setFieldValue(fieldProps.name, newValue)

          if (onChange) onChange(value)
        }}

        renderInput={(props) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              position: 'relative',
              '& .MuiInputBase-root': { borderRadius: ELEMENT_RADIUS_MID },
              '& input': { padding: '0.75rem !important', fontSize: '0.8rem' },
              ...sx,
            }}
            onClick={() => setOpen(true)}
            onMouseEnter={() => {
              setShowClear(true)
            }}
            onMouseLeave={() => {
              setShowClear(false)
            }}
          >
            <TextField
              // variant='standard'
              autoComplete='off'
              helperText={renderHelperText()}
              fullWidth
              {...props}
              {...field}
              {...fieldProps}
              inputProps={{
                ...props.inputProps,
                placeholder: '',
                readOnly: true,
              }}
            />
            {!disabled && field?.value && (
              <Button
                size='small'
                color='neutral'
                aria-label='clear'
                tabIndex={2}
                sx={{
                  borderRadius: '50%',
                  minWidth: '1.75rem',
                  fontSize: '2rem',
                  width: '2.2rem',
                  height: '2.2rem',
                  position: 'absolute',
                  top: '0.3rem',
                  right: '2.5rem',
                  zIndex: '300',
                }}
              >
                <Close
                  fontSize='inherit'
                  sx={{ fontSize: '1.4rem' }}
                  onClick={(e) => {
                    e.stopPropagation()

                    let newValue = null
                    
                    if (valueSelector) {
                      newValue = {
                        ...field.value,
                        [valueSelector]: null
                      }
                    } 

                    setFieldValue(fieldProps.name, newValue)
                    if (onChange) onChange(null)
                  }}
                />
              </Button>
            )}
          </Box>
        )}
      />
    </LocalizationProvider>
  )
}
export default DateTimePickerField
